import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiNotConfiguredError,
  createTenant,
  listTenants,
  type Tenant,
} from '../../lib/witness-api';
import { generateTenantId } from '../../lib/tenant-id';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; tenants: readonly Tenant[] };

export function PlatformTenantsPage() {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const reload = useCallback((signal?: AbortSignal) => {
    setState({ status: 'loading' });
    listTenants({}, signal ? { signal } : {})
      .then((tenants) => setState({ status: 'ready', tenants }))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const message =
          err instanceof ApiNotConfiguredError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'failed to load tenants';
        setState({ status: 'error', message });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="font-serif text-2xl tracking-tight text-neutral-900">
          Tenants
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          Every organization that acts on Witness — one row per tenant.
        </p>
      </div>

      <NewTenantForm onCreated={() => reload()} />

      <TenantsList state={state} onRetry={() => reload()} />
    </div>
  );
}

function TenantsList({
  state,
  onRetry,
}: {
  state: LoadState;
  onRetry: () => void;
}) {
  if (state.status === 'loading') {
    return <p className="text-sm text-neutral-500">Loading tenants…</p>;
  }
  if (state.status === 'error') {
    return (
      <div className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm">
        <p className="text-red-900">Failed to load tenants: {state.message}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 text-red-900 underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }
  if (state.tenants.length === 0) {
    return <p className="text-sm text-neutral-500">No tenants yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-neutral-500">
            <th className="py-2 pr-4">Display name</th>
            <th className="py-2 pr-4">Id</th>
            <th className="py-2 pr-4">Created</th>
          </tr>
        </thead>
        <tbody>
          {state.tenants.map((tenant) => (
            <tr key={tenant.id} className="border-b border-neutral-100">
              <td className="py-2 pr-4 text-neutral-900">
                <Link className="underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-900" to={`/platform/tenants/${tenant.id}`}>
                  {tenant.displayName}
                </Link>
              </td>
              <td className="py-2 pr-4 font-mono text-neutral-700">
                {tenant.id}
              </td>
              <td className="py-2 pr-4 text-neutral-500">
                {formatDate(tenant.createdAtMs)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string };

function NewTenantForm({ onCreated }: { onCreated: () => void }) {
  const [displayName, setDisplayName] = useState('');
  const [form, setForm] = useState<FormState>({ status: 'idle' });

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const trimmed = displayName.trim();
      if (!trimmed) return;
      setForm({ status: 'submitting' });
      try {
        const result = await createTenant({
          id: generateTenantId(),
          displayName: trimmed,
        });
        if (!result.ok) {
          setForm({
            status: 'error',
            message:
              result.reason === 'duplicate_id'
                ? 'Id collision — try again.'
                : 'Request rejected as invalid.',
          });
          return;
        }
        setDisplayName('');
        setForm({ status: 'idle' });
        onCreated();
      } catch (err) {
        const message =
          err instanceof ApiNotConfiguredError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Create failed';
        setForm({ status: 'error', message });
      }
    },
    [displayName, onCreated],
  );

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-3 rounded border border-neutral-200 bg-neutral-50 p-4"
    >
      <div className="flex flex-col gap-1">
        <label
          htmlFor="displayName"
          className="text-xs font-medium uppercase tracking-wider text-neutral-500"
        >
          New tenant display name
        </label>
        <input
          id="displayName"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. Bob's Law Firm"
          maxLength={200}
          className="rounded border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none"
          disabled={form.status === 'submitting'}
        />
      </div>
      {form.status === 'error' && (
        <p className="text-sm text-red-700">{form.message}</p>
      )}
      <div>
        <button
          type="submit"
          disabled={form.status === 'submitting' || !displayName.trim()}
          className="rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-neutral-50 hover:bg-neutral-700 disabled:cursor-not-allowed disabled:bg-neutral-400"
        >
          {form.status === 'submitting' ? 'Creating…' : 'Create tenant'}
        </button>
      </div>
    </form>
  );
}

function formatDate(ms: number): string {
  return new Date(ms).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}
